import { createContext, useState, useEffect, useCallback } from "react";
import i18n from "i18next";
import {
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
} from "../Helpers/Requests";
import endpoints from "../Helpers/Endpoints";
import { createOnboardingAppUrl } from "../Helpers/Actions";
import emitter from "../Utils/events/eventBus";
import { PAGE_VIEWS } from "../Constants";
import { communityRoutes, supplierRoutes } from "../Routes/routePaths";
// ⚠️ TEMP DEV-PHASE PREVIEW — remove with the KYC preview switcher.
import { readKycPreviewFlag, getPreviewKycStatus } from "../Constants/kycPreview";

const normalizePath = (path) => {
  if (!path) return "/";
  return path.replace(/\/+$/, "") || "/";
};

export const AppContext = createContext({});

export const AppProvider = ({ children }) => {
  const [sidebarData, setSidebarData] = useState({});
  const [languages, setLanguages] = useState([]);
  const [isCommunityManager, setisCommunityManager] = useState(false);
  const [isCommunityManagementRole, setisCommunityManagementRole] =
    useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState({});
  const [getStartedContent, setGetStartedContent] = useState({});
  const [userCommunities, setUserCommunities] = useState([]);
  // Where the supplier stands in Grow's business verification. Everything that
  // reaches a community reads this one value; the backend serves it on the
  // sidebar payload as `kyc_status`.
  const [kycStatus, setKycStatus] = useState(null);

  /**
   * Re-read the supplier's KYC state. Grow's form is filled in inside a window
   * of ours and submitted to them directly, so the only way we learn it moved
   * on is by asking — which is what the details window does when it closes.
   */
  const refreshKycStatus = useCallback(async () => {
    try {
      const response = await apiGetRequest(endpoints.getSidebarData);
      setKycStatus(response?.data?.kyc_status ?? null);
    } catch (error) {
      // Leave the state as it is — a failed refresh must not open anything up.
    }
  }, []);
  const [systemError, setSystemError] = useState(null); // New state for system errors

  const fetchGetStartedContent = useCallback(async () => {
    try {
      const url = `${endpoints.getStartedTasks}`;
      const response = await apiGetRequest(url);
      // ⚠️ TEMP (FE-first "Grow your community") — the backend never sends
      // weeklyTasksPreview, so keep it across refetches (e.g. clicking the
      // sidebar card) or the weekly card flips back to Get Started.
      setGetStartedContent((prev) =>
        prev?.weeklyTasksPreview
          ? { ...response.data, weeklyTasksPreview: true }
          : response.data,
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch get started content:", error);
      return null;
    }
  }, []);

  const fetchUserCommunities = useCallback(async () => {
    try {
      const url = `${endpoints.getUserCommunities}`;
      const response = await apiGetRequest(url);
      const communities = response.data.communities;
      setUserCommunities(communities);
    } catch (error) {
      console.error("Failed to fetch user communities:", error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentRoutePathName = normalizePath(window.location.pathname);
        // ⚠️ TEMP DEV-PHASE PREVIEW — see the KYC block below.
        const isKycPreview = readKycPreviewFlag();

        const isManagersHubRoute = currentRoutePathName === "/managers-hub";

        // TEMP dev-only preview seed for the Manager-of-Managers hub FE-first
        // preview — lets /managers-hub render without auth/backend, in the real
        // CM shell (the sidebar portfolio entries need multiple communities).
        // Opt-in via `?preview=1` only: seeding it for real users replaced their
        // account with a fake CM-only one, so they couldn't get back to their
        // supplier / community views. REMOVE with the feature.
        const hubParams = new URLSearchParams(window.location.search);
        if (isManagersHubRoute && hubParams.get("preview") === "1") {
          const params = hubParams;
          const lang = params.get("lang") === "en" ? "en" : "he";
          if (localStorage.getItem("appLanguage") !== lang) {
            localStorage.setItem("appLanguage", lang);
            i18n.changeLanguage(lang);
            window.location.reload();
            return;
          }
          localStorage.setItem("isCommunityManagementRole", "true");
          setisCommunityManagementRole(true);
          setSidebarData({
            communityName: "מגדלי נווה שאנן",
            communityImage: "/mysupplier.png",
            last_role_view: "COMMUNITY_MANAGER",
            communitiesTosUpdates: [],
            // Mock role flags — a Manager-of-Managers, currently in MoM view.
            // In prod these come from the backend (isMoM is Keilot-granted).
            isMoM: true,
            momView: true,
            isCommunityManager: true,
          });
          setUserCommunities([
            { id: 1, name: "מגדלי נווה שאנן", image_url: "/mysupplier.png" },
            { id: 2, name: "מעונות פארק הירקון", image_url: "/supplier-icon.png" },
            { id: 3, name: "כפר סבא ירוקה", image_url: "/people.png" },
          ]);
          setIsLoading(false);
          return;
        }

        // ⚠️ TEMP dev-only preview for "Grow your community" (FE-first) — lets
        // the page, the Get Started popup and the dashboard card be clicked
        // through without auth/backend. `weeklyTasksPreview` is never sent by the backend, so real
        // users keep today's Get Started. Opt-in via `?preview=1` only: seeding
        // it for real users swapped their community for the fake one on every
        // full load (e.g. the week switcher). Remove at graduation.
        const isGrowCommunityRoute = currentRoutePathName === "/grow-community";
        const growParams = new URLSearchParams(window.location.search);
        if (isGrowCommunityRoute && growParams.get("preview") === "1") {
          const params = growParams;
          // RTL is driven by localStorage.appLanguage, read once at startup
          // (DirectionContext) — set it and reload once so Hebrew lays out RTL.
          const previewLang = params.get("lang") === "en" ? "en" : "he";
          if (localStorage.getItem("appLanguage") !== previewLang) {
            localStorage.setItem("appLanguage", previewLang);
            window.location.reload();
            return;
          }
          i18n.changeLanguage(previewLang);
          document.documentElement.lang = previewLang;
          setSidebarData({ communityName: "מגדלי נווה שאנן", last_role_view: "COMMUNITY_MANAGER" });
          setisCommunityManager(true);
          localStorage.setItem("isCommunityManager", true);
          setisCommunityManagementRole(true);
          localStorage.setItem("isCommunityManagementRole", true);
          setUserCommunities([{ id: 1, name: "מגדלי נווה שאנן", image_url: "/mysupplier.png" }]);
          // Setup is complete here; the page's own setup-incomplete mock
          // (`?setup=1`) only affects the Grow page's setup card.
          setGetStartedContent({
            weeklyTasksPreview: true,
            getStartedTasks: { communityShineCompleted: true, supplierCount: { current: 5, target: 3, completed: true }, percentageComplete: 100 },
          });
          return;
        }

        // ⚠️ TEMP dev-only preview for supplier KYC — walks the real supplier
        // app (sidebar, communities index, my communities) through the three
        // KYC states without auth/backend. Opt-in via `?kycPreview=1`, which
        // is remembered so the pages can be clicked between; `?kycPreview=0`
        // turns it off. Remove at graduation.
        if (isKycPreview) {
          const previewLang =
            new URLSearchParams(window.location.search).get("lang") === "en"
              ? "en"
              : "he";
          if (localStorage.getItem("appLanguage") !== previewLang) {
            localStorage.setItem("appLanguage", previewLang);
            window.location.reload();
            return;
          }
          i18n.changeLanguage(previewLang);
          document.documentElement.lang = previewLang;
          localStorage.setItem("isCommunityManagementRole", "false");
          setisCommunityManagementRole(false);
          setSidebarData({
            supplierName: "מאפיית לחם הארץ",
            communityName: "מאפיית לחם הארץ",
            last_role_view: "SUPPLIER",
          });
          setKycStatus(getPreviewKycStatus());
          setIsLoading(false);
          return;
        }

        const isHomeRoute = currentRoutePathName === "/";

        if (!isHomeRoute) {
          const isSupplierRoute = supplierRoutes.some(
            (route) => normalizePath(route.path) === currentRoutePathName,
          );

          const isCommunityManagerRoute = communityRoutes.some(
            (route) => normalizePath(route.path) === currentRoutePathName,
          );

          // /dashboard is registered in BOTH route lists — it is one route
          // rendering whichever dashboard matches the current role. Toggling on
          // a shared route would decide the role FOR the user, and since the
          // supplier branch is tested first a manager who also sells was
          // flipped to SUPPLIER on every visit, making the community-manager
          // dashboard unreachable. Shared routes must leave the role alone.
          const isSharedRoute = isSupplierRoute && isCommunityManagerRoute;

          if (!isSharedRoute && (isSupplierRoute || isCommunityManagerRoute)) {
            const pageView = isSupplierRoute
              ? PAGE_VIEWS.SUPPLIER_MANAGEMENT_VIEW
              : PAGE_VIEWS.COMMUNITY_MANAGEMENT_VIEW;
            await apiPostRequest(endpoints.toggleRoleByPageView, {
              pageView,
            });
          }
        }

        const [sidebarResponse, languagesResponse, profileResponse] =
          await Promise.all([
            apiGetRequest(endpoints.getSidebarData),
            apiGetRequest(endpoints.getAllLanguages),
            apiGetRequest(endpoints.getProfileData),
            fetchGetStartedContent(),
          ]);

        // ⚠️ TEMP (FE-first) — on the Grow page real accounts load as usual; the
        // weekly-tasks sidebar/dashboard card (weeklyTasksPreview) is only on in
        // the full `?preview=1` seed above, and the setup-incomplete mock lives
        // in the Grow page alone. Remove at graduation.

        const sidebar = sidebarResponse?.data || {};
        const langs = languagesResponse?.data?.data || [];
        const profile = profileResponse?.data || {};

        // The hub is the Manager-of-Managers view — portfolio-only sidebar —
        // for users the backend flags as MoM.
        setSidebarData(
          isManagersHubRoute && sidebar.isMoM ? { ...sidebar, momView: true } : sidebar,
        );
        setLanguages(langs);
        setProfileData(profile);
        setKycStatus(sidebar.kyc_status ?? null);

        const currentLang = langs
          .find((l) => l.name === sidebar.language)
          ?.code?.trim();
        if (currentLang) {
          i18n.changeLanguage(currentLang);
          localStorage.setItem("appLanguage", currentLang);
          document.documentElement.lang = currentLang;
        }

        if (sidebar.last_role_view !== "SUPPLIER") {
          await fetchUserCommunities();
        }

        const isUserAdmin = sidebar?.last_role_view === "COMMUNITY_MANAGER";
        setisCommunityManager(isUserAdmin);
        localStorage.setItem("isCommunityManager", isUserAdmin);

        const isCMRole =
          sidebar?.last_role_view === "COMMUNITY_MANAGER" ||
          sidebar?.last_role_view === "COMMUNITY_MODERATOR";
        setisCommunityManagementRole(isCMRole);
        localStorage.setItem("isCommunityManagementRole", isCMRole);

        if (sidebar.showWelcomePopup) {
          setShowWelcomePopup(true);
        }
        if (sidebar.showUpdatePopup) {
          setShowUpdatePopup(true);
        }
      } catch (error) {
        console.error(error);
        // Check for 401 or 404 errors in any of the Promise.all calls
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 404)
        ) {
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 404)
          ) {
            window.location.href = createOnboardingAppUrl({ path: "/welcome" });
          } else if (
            error.response &&
            (error.response.status === 500 || error.response.status === 502)
          ) {
            setSystemError("We are facing issues");
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [fetchGetStartedContent, fetchUserCommunities]);

  const updateLanguage = useCallback(
    async (languageId) => {
      try {
        await apiPutRequest(endpoints.updateLanguage, { languageId });
        const lang = languages.find((l) => l.id === languageId)?.code?.trim();
        if (lang) {
          i18n.changeLanguage(lang);
          localStorage.setItem("appLanguage", lang);
          document.documentElement.lang = lang;
        }
      } catch (error) {
        console.error("Failed to update language:", error);
        // Check for 401 or 404 errors in any of the Promise.all calls
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 404)
        ) {
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 404)
          ) {
            window.location.href = createOnboardingAppUrl({ path: "/welcome" });
          } else if (
            error.response &&
            (error.response.status === 500 || error.response.status === 502)
          ) {
            setSystemError("We are facing issues");
          }
        }
      }
    },
    [languages],
  );

  // Re-check for update popup when user returns to the tab
  const checkForUpdatePopup = useCallback(async () => {
    try {
      const response = await apiGetRequest(endpoints.getSidebarData);
      const sidebar = response?.data || {};
      if (sidebar.showUpdatePopup) {
        setShowUpdatePopup(true);
      }
    } catch (error) {
      // Silently fail - not critical
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdatePopup();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    emitter.on("fetch-get-started-content", fetchGetStartedContent);
    emitter.on("check-update-popup", checkForUpdatePopup);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      emitter.off("fetch-get-started-content", fetchGetStartedContent);
      emitter.off("check-update-popup", checkForUpdatePopup);
    };
  }, [fetchGetStartedContent, checkForUpdatePopup]);

  return (
    <AppContext.Provider
      value={{
        sidebarData,
        isCommunityManager,
        isCommunityManagementRole,
        languages,
        updateLanguage,
        showWelcomePopup,
        setShowWelcomePopup,
        showUpdatePopup,
        setShowUpdatePopup,
        isLoading,
        profileData,
        getStartedContent,
        userCommunities,
        fetchGetStartedContent,
        kycStatus,
        refreshKycStatus,
        // ⚠️ TEMP DEV-PHASE PREVIEW — only the KYC preview switcher writes this.
        setKycStatus,
        systemError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
